import { useState } from "react";
import { v4 as uuid } from "uuid";
import config from "config/config";
import { sha384 } from "crypto-hash";
import Countdown from "react-countdown";
import { Link } from "react-router-dom";

import { getUserProfile } from "service/userservice/getUserProfile";
import { sendEmailNotification } from "service/notificationservice/sendEmailNotification";

import { useForm } from "react-hook-form";
import { useDynamicSecurityQuestionsOptions } from "hooks/useDynamicSecurityQuestionsOptions";
import { isFlagEnabled } from "js/utilities";
import { useTranslation, Trans } from "react-i18next";

import FormInputError from "components/General/FormInputError";
import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import SSNInput from "components/General/Form/SSNInput";
import DomainErrorModal from "components/ModalDialogs/DomainErrorModal";

export default function AgentForgotUsername() {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    reValidateMode: "onBlur",
    shouldUnregister: false,
    defaultValues: {
      ssn: "",
      phoneNumber: "",
    },
  });

  const { t } = useTranslation();
  const UUID = uuid();
  const [step, setStep] = useState(1);
  const [agentType, setAgentType] = useState("agent");
  const [currentCounterTime, setCurrentCounterTime] = useState(null);
  const [passcodeExpired, setPasscodeExpired] = useState(false);
  const [currentCounterIndex, setCurrentCounterIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [userName, setUserName] = useState("");
  const [name, setName] = useState("");
  const [confirmationPassCode, setConfirmationPassCode] = useState("");
  const [errorInfo, seterrorInfo] = useState("");
  const [modalState, setModalState] = useState(false);
  const [question1, setQuestion1] = useState(0);
  const [question2, setQuestion2] = useState(0);
  const [question3, setQuestion3] = useState(0);
  const [answer1, setAnswer1] = useState("");
  const [answer2, setAnswer2] = useState("");
  const [answer3, setAnswer3] = useState("");
  const [inputType, setInputType] = useState("agentnumber");
  const onInputTypeOptionChange = (e) => {
    setInputType(e.target.value);
  };

  const onOptionChange = (e) => {
    setInputType(inputType);
    setAgentType(e.target.value);
  };

  const renderer = ({ minutes, seconds, completed }) => {
    if (completed) {
      setPasscodeExpired(true);
      return (
        <Link
          to=""
          className="mb-4 mr-5"
          onClick={async () => {
            let passcode = randomNumberInRange(100000, 999999);
            let securitycode = randomNumberInRange(100000, 999999);
            let sec =
              parseInt(securitycode.toString().substring(3, 4)) === 0
                ? 1
                : parseInt(securitycode.toString().substring(3, 4));
            let confirmationcode = passcode * sec;
            setConfirmationPassCode(passcode.toString());
            setLoading(true);
            let contentDictionary = {};
            contentDictionary.confirmationcode = confirmationcode.toString();
            contentDictionary.username = name;
            contentDictionary.securitycode = securitycode.toString();

            await sendEmailNotification(userEmail, 12, contentDictionary).catch(
              (error) => {
                console.log(error);
              }
            );
            setLoading(false);
            setCurrentCounterTime(
              Date.now() + parseInt(config.OTP_EXPIRY_TIME)
            );
            setCurrentCounterIndex(currentCounterIndex + 1);
            setPasscodeExpired(false);
          }}
        >
          Resend Code
        </Link>
      );
    } else {
      return (
        <p className="text-sm">
          Code expires in {minutes > 9 ? minutes : "0" + minutes}:
          {seconds > 9 ? seconds : "0" + seconds}
        </p>
      );
    }
  };

  function randomNumberInRange(min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  async function onSubmit(data) {
    if (step === 1) {
      setLoading(true);
      await getUserProfile(
        "",
        "agent",
        agentType === "agent" && inputType === "agentnumber"
          ? data.portalId
          : "",
        agentType === "agent" && inputType === "sinnumber" ? data.ssn : "",
        agentType === "agent" ? "" : data.emailId,
        "",
        agentType === "agent" && inputType === "sinnumber"
          ? data.firstname
          : "",
        agentType === "agent" && inputType === "sinnumber" ? data.lastname : "",
        agentType === "agent-administrative-assistant" &&
          isFlagEnabled("ENABLE_AGENT_ASSISTANT_AND_LSP_REGISTRATION")
          ? data.AgentId
          : ""
      )
        .then((userProfile) => {
          setLoading(false);
          if (userProfile === null) {
            seterrorInfo(t("ResetPassword-error-1"));
            setModalState(true);
          } else {
            setUserEmail(userProfile.emailId);
            setUserName(userProfile.userName);
            setName(userProfile.firstName);
            setQuestion1(userProfile.userSecurityAnswers[0].questionId);
            setQuestion2(userProfile.userSecurityAnswers[1].questionId);
            setQuestion3(userProfile.userSecurityAnswers[2].questionId);
            setAnswer1(userProfile.userSecurityAnswers[0].userAnswer);
            setAnswer2(userProfile.userSecurityAnswers[1].userAnswer);
            setAnswer3(userProfile.userSecurityAnswers[2].userAnswer);
            setStep(step + 1);
          }
        })
        .catch((error) => {
          setLoading(false);
          if (error.status && error.status === 403) {
            seterrorInfo(error.title);
            setModalState(true);
          } else {
            seterrorInfo(t("ResetPassword-error-1"));
            setModalState(true);
            console.log(error);
          }
        });
    } else if (step === 2) {
      let enteredanswer1 = (
        await sha384(
          data.securtityAnswerOne.toLowerCase().replaceAll(/\s/g, "")
        )
      )
        .toString()
        .substring(30, 45);

      let enteredanswer2 = (
        await sha384(
          data.securtityAnswerTwo.toLowerCase().replaceAll(/\s/g, "")
        )
      )
        .toString()
        .substring(30, 45);

      let enteredanswer3 = (
        await sha384(
          data.securtityAnswerThree.toLowerCase().replaceAll(/\s/g, "")
        )
      )
        .toString()
        .substring(30, 45);

      if (
        (answer1.toLowerCase() === enteredanswer1 &&
          answer2.toLowerCase() === enteredanswer2) ||
        (answer2.toLowerCase() === enteredanswer2 &&
          answer3.toLowerCase() === enteredanswer3) ||
        (answer1.toLowerCase() === enteredanswer1 &&
          answer3.toLowerCase() === enteredanswer3)
      ) {
        let passcode = randomNumberInRange(100000, 999999);
        let securitycode = randomNumberInRange(100000, 999999);
        let sec =
          parseInt(securitycode.toString().substring(3, 4)) === 0
            ? 1
            : parseInt(securitycode.toString().substring(3, 4));
        let confirmationcode = passcode * sec;
        setConfirmationPassCode(passcode.toString());
        setLoading(true);
        let contentDictionary = {};
        contentDictionary.confirmationcode = confirmationcode.toString();
        contentDictionary.username = name;
        contentDictionary.securitycode = securitycode.toString();

        await sendEmailNotification(userEmail, 12, contentDictionary).catch(
          (error) => {
            console.log(error);
          }
        );

        setLoading(false);
        setStep(step + 1);
        setCurrentCounterTime(Date.now() + parseInt(config.OTP_EXPIRY_TIME));
      } else {
        seterrorInfo(t("ResetPassword-error-2"));
        setModalState(true);
      }
    } else if (step === 3) {
      if (passcodeExpired) {
        seterrorInfo(t("ResetPassword-error-3"));
        setModalState(true);
      } else {
        if (
          data.resetCode === confirmationPassCode ||
          (config.ENABLE_DEFAULT_OTP === "true" &&
            data.resetCode === config.DEFAULT_OTP)
        ) {
          setStep(step + 1);
        } else {
          seterrorInfo(t("ResetPassword-error-4"));
          setModalState(true);
        }
      }
    }
  }

  const {
    setSecurityQuestionOneId,
    securityQuestionsOne,

    setSecurityQuestionTwoId,
    securityQuestionsTwo,

    setSecurityQuestionThreeId,
    securityQuestionsThree,
  } = useDynamicSecurityQuestionsOptions();

  return (
    <>
      <EverlakeLoadingOverlay isLoading={loading}>
        {step === 1 && (
          <div className="container ui-component-spacing">
            <div className="ui-card p-4 mx-auto  lg:w-3/4 md:p-6">
              <form
                onSubmit={handleSubmit(onSubmit)}
                aria-label="Forgot username form"
              >
                <h2>{t("ForgotUserName-title")}</h2>

                <fieldset className="mt-1">
                  <legend className="text-sm mb-2">
                    {t("ForgotUserName-selectRole")}
                  </legend>
                  <div className="inline-block">
                    <label className="inline-flex items-center text-sm font-normal mr-6">
                      <input
                        type="radio"
                        value="agent"
                        className="mr-2"
                        checked={agentType === "agent"}
                        onClick={onOptionChange}
                        {...register("accountType", {
                          required: "Please select your account type",
                        })}
                      />
                      {t("ForgotUserName-role-agent")}
                    </label>
                  </div>
                  <div className="inline-block mr-6">
                    <label className="inline-flex items-center text-sm font-normal">
                      <input
                        type="radio"
                        value="agent-administrative-assistant"
                        className="mr-2"
                        onClick={onOptionChange}
                        checked={agentType === "agent-administrative-assistant"}
                        {...register("accountType", {
                          required: "Please select your account type",
                        })}
                      />
                      {t("ForgotUserName-role-agentAdministrativeAssistant")}
                    </label>
                  </div>

                  {errors.accountType && (
                    <FormInputError
                      id={`${UUID}-accountType-error`}
                      errorMessage={errors.accountType.message}
                    />
                  )}
                </fieldset>

                <hr />

                {agentType === "agent-administrative-assistant" && (
                  <>
                    <p className="mb-6 font-semibold text-base">
                      {t(
                        "ForgotUserName-helpText-agentAdministrativeAssistant"
                      )}
                    </p>
                    {isFlagEnabled(
                      "ENABLE_AGENT_ASSISTANT_AND_LSP_REGISTRATION"
                    ) && (
                      <div className="mb-4">
                        <label
                          htmlFor={`${UUID}-AgentId`}
                          className="text-base font-semibold"
                        >
                          {t("ForgotUserName-formLabel-masterAgentId")}
                        </label>
                        <p className="text-xs mb-1">
                          {t("ForgotUserName-formLabel-masterAgentId-helpText")}
                        </p>
                        <input
                          id={`${UUID}-AgentId`}
                          className="w-full"
                          type="text"
                          placeholder="#"
                          {...register("AgentId", {
                            required: {
                              value: true,
                              message:
                                "Please enter your master agent Id number",
                            },
                          })}
                          aria-labelledby={`${UUID}-AgentId-portal`}
                        />
                        {errors.AgentId && (
                          <FormInputError
                            id={`${UUID}-portalId`}
                            errorMessage={errors.AgentId.message}
                          />
                        )}
                      </div>
                    )}
                    <div>
                      <label
                        htmlFor={`${UUID}-emailId`}
                        className="text-base font-semibold"
                      >
                        {t("ForgotUserName-formLabel-email")}
                      </label>
                      <input
                        id={`${UUID}-emailId`}
                        className="w-full"
                        type="text"
                        placeholder="#"
                        {...register("emailId", {
                          required: {
                            value: true,
                            message: "Please enter your email address",
                          },
                        })}
                        aria-describedby={`${UUID}-emailId-error`}
                        aria-labelledby={`${UUID}-Emailid`}
                      />

                      {errors.emailId && (
                        <FormInputError
                          id={`${UUID}-emailId-error`}
                          errorMessage={errors.emailId.message}
                        />
                      )}
                    </div>
                  </>
                )}

                {agentType === "agent" && (
                  <>
                    <p className="mb-4 font-semibold text-base">
                      {t("ForgotUserName-helpText-agent")}
                    </p>
                    <fieldset>
                      <legend className="sr-only">Select an input type</legend>
                      <div className="inline-block mt-1 mr-6 mb-4">
                        <label className="inline-flex items-center text-sm font-normal">
                          <input
                            type="radio"
                            name="inputType_AID"
                            value="agentnumber"
                            checked={inputType === "agentnumber"}
                            onClick={onInputTypeOptionChange}
                            id="ID_inputType_AID"
                            {...register("inputType")}
                            className="mr-2"
                          />
                          {t("ForgotUserName-agentIdSelection")}
                        </label>
                      </div>
                      <div className="inline-block">
                        <label className="inline-flex items-center text-sm font-normal">
                          <input
                            type="radio"
                            name="inputType_SIN"
                            value="sinnumber"
                            id="ID_inputType_SIN"
                            checked={inputType === "sinnumber"}
                            onClick={onInputTypeOptionChange}
                            {...register("inputType")}
                            className="mr-2"
                          />
                          {t("ForgotUserName-ssnSelection")}
                        </label>
                      </div>
                    </fieldset>
                    {inputType === "agentnumber" && (
                      <div>
                        <label
                          id={`${UUID}-agent-portal-id`}
                          className="text-base font-semibold"
                        >
                          {t("ForgotUserName-formLabel-agentId")}
                        </label>
                        <p className="text-xs mb-1">
                          {t("ForgotUserName-formLabel-agentId-helpText")}
                        </p>
                        <input
                          id={`${UUID}-portalId`}
                          className="w-full"
                          type="text"
                          placeholder="#"
                          {...register("portalId", {
                            required: {
                              value: true,
                              message: "Please enter your agent Id number",
                            },
                          })}
                          aria-labelledby={`${UUID}-agent-portal-id`}
                        />
                        {errors.portalId && (
                          <FormInputError
                            id={`${UUID}-portalId`}
                            errorMessage={errors.portalId.message}
                          />
                        )}
                      </div>
                    )}

                    {inputType === "sinnumber" && (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                        <div>
                          <div className="relative">
                            <label id={`${UUID}-ssn-label`}>
                              {t("ForgotUserName-formLabel-ssn")}
                            </label>

                            <SSNInput
                              name="ssn"
                              describedby={`${UUID}-ssn-label`}
                              register={register}
                              setValue={setValue}
                            />

                            {errors.ssn && (
                              <FormInputError
                                id={`${uuid}-ssn`}
                                errorMessage={errors.ssn.message}
                              />
                            )}
                          </div>
                          <div
                            id={`${uuid}-ssnnote`}
                            className="text-xxs text-theme-text-tertiary mt-1"
                          >
                            {t("ForgotUserName-formLabel-ssn-helpText")}
                          </div>{" "}
                        </div>
                        <div>
                          <label id={`${uuid}-firstname-label`}>
                            {t("ForgotUserName-formLabel-firstname")}
                          </label>
                          <input
                            id={`${uuid}-firstname`}
                            className="w-full"
                            type="text"
                            {...register("firstname", {
                              required: "Please enter your first name",
                              maxLength: {
                                value: 50,
                                message:
                                  "Please enter less than 50 characters for your firstname",
                              },
                              pattern: {
                                value: /^[A-Za-z-' ]{1,50}$/,
                                message:
                                  "Please enter only [alphabets - '] characters",
                              },
                            })}
                            aria-describedby={`${uuid}-firstname-error`}
                            aria-labelledby={`${uuid}-firstname-label`}
                          />
                          {errors.firstname && (
                            <FormInputError
                              id={`${uuid}-firstname-error`}
                              errorMessage={errors.firstname.message}
                            />
                          )}
                        </div>

                        <div>
                          <label id={`${uuid}-lastname-label`}>
                            {t("ForgotUserName-formLabel-lastname")}
                          </label>
                          <input
                            id={`${uuid}-lastname`}
                            className="w-full"
                            type="text"
                            {...register("lastname", {
                              required: "please enter your last name",
                              maxLength: {
                                value: 50,
                                message:
                                  "Please enter less than 50 characters for your lastname",
                              },
                              pattern: {
                                value: /^[A-Za-z-' ]{1,50}$/,
                                message:
                                  "Please enter only [alphabets - '] characters",
                              },
                            })}
                            aria-describedby={`${uuid}-lastname-error`}
                            aria-labelledby={`${uuid}-lastname-label`}
                          />
                          {errors.lastname && (
                            <FormInputError
                              id={`${uuid}-lastname-error`}
                              errorMessage={errors.lastname.message}
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
                <div className="mt-4 text-right">
                  <button className="ui-btn-primary ui-btn-min-w mt-2">
                    {t("ForgotUserName-continueButton")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {step === 2 && (
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="container ui-component-spacing">
              <div className="ui-card p-4 mx-auto sm:w-2/3 md:w-3/5 lg:w-1/2 md:p-6">
                <h2 className="font-body font-bold text-xl">
                  {t("ForgotUserName-step2-title")}
                </h2>
                <div className="mt-4 md:mt-6">
                  <label id={`${UUID}-ID_question1Label`}>
                    {t("ResetPassword-label-2")}
                  </label>
                  <select
                    disabled
                    id="ID_question1"
                    className="w-full"
                    value={question1}
                    isOptionDisabled={true}
                    {...register("securityQuestionOne", {
                      onChange: (e) =>
                        setSecurityQuestionOneId(parseInt(e.target.value)),
                    })}
                    aria-describedby={`${UUID}-ID_question1Error`}
                    aria-labelledby={`${UUID}-ID_question1Label`}
                  >
                    <option value="">Select a security question</option>
                    {securityQuestionsOne.map((question) => (
                      <option
                        key={`security question - ${question.id}`}
                        value={question.id}
                      >
                        {question.question}
                      </option>
                    ))}
                  </select>
                  {errors.securityQuestionOne && (
                    <FormInputError
                      id={`${UUID}-question1-error`}
                      errorMessage={errors.securityQuestionOne.message}
                    />
                  )}
                </div>
                <div className="mt-4 md:mt-6">
                  <label id={`${UUID}-ID_answer1Label`}>
                    {t("ResetPassword-label-4")}
                  </label>
                  <input
                    id="ID_answer1"
                    className="w-full"
                    type="text"
                    {...register("securtityAnswerOne", {
                      required: {
                        value: true,
                        message: "Please answer selected security question",
                      },
                    })}
                    aria-describedby={`${UUID}-ID_answer1Error`}
                    aria-labelledby={`${UUID}-ID_answer1Label`}
                  />
                  {errors.securtityAnswerOne && (
                    <FormInputError
                      id={`${UUID}-answer1-error`}
                      errorMessage={errors.securtityAnswerOne.message}
                    />
                  )}
                </div>

                <div className="mt-4 md:mt-6">
                  <label id={`${UUID}-ID_question2Label`}>
                    {t("ResetPassword-label-5")}
                  </label>
                  <select
                    disabled
                    id="ID_question2"
                    className="w-full"
                    value={question2}
                    {...register("securityQuestionTwo", {
                      onChange: (e) =>
                        setSecurityQuestionTwoId(parseInt(e.target.value)),
                    })}
                    aria-describedby={`${UUID}-ID_question2Error`}
                    aria-labelledby={`${UUID}-ID_question2Label`}
                  >
                    <option value="">Select a security question</option>
                    {securityQuestionsTwo.map((question) => (
                      <option
                        key={`security question - ${question.id}`}
                        value={question.id}
                      >
                        {question.question}
                      </option>
                    ))}
                  </select>
                  {errors.securityQuestionTwo && (
                    <FormInputError
                      id={`${UUID}-question2-error`}
                      errorMessage={errors.securityQuestionTwo.message}
                    />
                  )}
                </div>
                <div className="mt-4 md:mt-6">
                  <label id={`${UUID}-ID_answer2Label`}>
                    {t("ResetPassword-label-13")}
                  </label>
                  <input
                    id="ID_answer2"
                    className="w-full"
                    type="text"
                    {...register("securtityAnswerTwo", {
                      required: {
                        value: true,
                        message: "Please answer selected security question",
                      },
                    })}
                    aria-describedby={`${UUID}ID_answer2Error`}
                    aria-labelledby={`${UUID}-ID_answer2Label`}
                  />
                  {errors.securtityAnswerTwo && (
                    <FormInputError
                      id={`${UUID}-answer2-error`}
                      errorMessage={errors.securtityAnswerTwo.message}
                    />
                  )}
                </div>

                <div className="mt-4 md:mt-6">
                  <label id={`${UUID}-ID_question3Label`}>
                    {t("ResetPassword-label-6")}
                  </label>
                  <select
                    disabled
                    id="ID_question3"
                    className="w-full"
                    value={question3}
                    {...register("securityQuestionThree", {
                      onChange: (e) =>
                        setSecurityQuestionThreeId(parseInt(e.target.value)),
                    })}
                    aria-describedby={`${UUID}-ID_question3Error`}
                    aria-labelledby={`${UUID}-ID_question3Label`}
                  >
                    <option value="">Select a security question</option>
                    {securityQuestionsThree.map((question) => (
                      <option
                        key={`security question - ${question.id}`}
                        value={question.id}
                      >
                        {question.question}
                      </option>
                    ))}
                  </select>
                  {errors.securityQuestionThree && (
                    <FormInputError
                      id={`${UUID}-Question3-error`}
                      errorMessage={errors.securityQuestionThree.message}
                    />
                  )}
                </div>
                <div className="mt-4 md:mt-6">
                  <label id={`${UUID}-ID_answer3Label`}>
                    {t("ResetPassword-label-7")}
                  </label>
                  <input
                    id="ID_answer3"
                    className="w-full"
                    type="text"
                    {...register("securtityAnswerThree", {
                      required: {
                        value: true,
                        message: "Please answer selected security question",
                      },
                    })}
                    aria-describedby={`${UUID}-ID_answer3Error`}
                    aria-labelledby={`${UUID}-ID_answer3Label`}
                  />
                  {errors.securtityAnswerThree && (
                    <FormInputError
                      id={`${UUID}-answer3-error`}
                      errorMessage={errors.securtityAnswerThree.message}
                    />
                  )}
                </div>
                <div className="text-center mt-4 md:mt-6">
                  <button className="ui-btn-primary ui-btn-min-w">
                    {t("ForgotUserName-continueButton")}
                  </button>
                </div>
              </div>
            </div>
          </form>
        )}

        {step === 3 && (
          <div className="container ui-component-spacing">
            <div className="ui-card p-4 mx-auto  lg:w-3/4 md:p-6">
              <h2>{t("ForgotUserName-step2-title")}</h2>
              <p className="text-sm">
                <Trans
                  i18nKey="ForgotUserName-step3-helpText"
                  values={{
                    minute: parseInt(parseInt(config.OTP_EXPIRY_TIME) / 60000),
                  }}
                />
              </p>

              <form onSubmit={handleSubmit(onSubmit)} className="mt-6">
                <div>
                  <label
                    id={`${UUID}-pw-resetcode-label`}
                    className="text-base font-semibold"
                  >
                    {t("ForgotUserName-username-reset-code")}
                  </label>

                  <input
                    id={`${UUID}-pw-resetcode`}
                    className="w-full"
                    type="text"
                    placeholder="Enter one-time code found in your email"
                    autoComplete="off"
                    {...register("resetCode", {
                      required: "Please enter your password reset code",
                    })}
                    aria-describedby={`${UUID}-pw-resetcode-error`}
                    aria-labelledby={`${UUID}-pw-resetcode-label`}
                  />

                  {errors.resetCode && (
                    <FormInputError
                      id={`${UUID}-pw-resetcode-error`}
                      errorMessage={errors.resetCode.message}
                    />
                  )}
                </div>
                <div className="mt-4 md:mt-6">
                  <Countdown
                    date={currentCounterTime}
                    key={currentCounterIndex}
                    renderer={renderer}
                  />
                </div>
                <div className="mt-4 text-right">
                  <button className="ui-btn-primary ui-btn-min-w mt-2">
                    {t("ForgotUserName-continueButton")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="container ui-component-spacing">
            <div className="ui-card p-4 mx-auto sm:w-2/3 md:w-3/5 lg:w-1/2 md:p-6">
              <h2>{t("ForgotUserName-step4-title")}</h2>
              <p className="text-sm">{t("ForgotUserName-step4-subtitle")}</p>
              <div className="text-xl text-center font-semibold mt-4 md:mt-6">
                {userName}
              </div>
              <div className="text-center mt-4 md:mt-6">
                <Link to="/">
                  <button className="ui-btn-primary ui-btn-min-w">
                    {t("ForgotUserName-toLogin")}
                  </button>
                </Link>
                <hr />
                <Link to="/forgot-password" className="text-xs">
                  {t("ForgotUserName-toForgotPassword")}
                </Link>
              </div>
            </div>
          </div>
        )}
        <DomainErrorModal
          modalState={modalState}
          setModalState={setModalState}
          errorInfo={errorInfo}
        />
      </EverlakeLoadingOverlay>
    </>
  );
}
